import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ArrowRight, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { STRIPE_PRODUCTS } from "@/lib/products";

/**
 * OrderForm – "Beställ Redoc AML & KYC"
 * Visitor picks one of the Stripe products and is taken directly to
 * Stripe Checkout (the rest of the purchase happens on Stripe's side).
 */
export default function OrderForm() {
  const [productId, setProductId] = useState("");

  const onSubmit = (e) => {
    e.preventDefault();
    if (!productId) {
      toast.error("Välj en produkt/tjänst att beställa.");
      return;
    }
    const product = STRIPE_PRODUCTS.find((p) => p.id === productId);
    if (!product) return;
    window.location.href = product.stripeUrl;
  };

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="l1-order-form">
      <div>
        <Label className="text-sm font-semibold mb-3 block text-slate-900">
          Välj produkt / tjänst *
        </Label>
        <RadioGroup
          value={productId}
          onValueChange={setProductId}
          className="space-y-2"
          data-testid="l1-order-products"
        >
          {STRIPE_PRODUCTS.map((p) => {
            const checked = productId === p.id;
            return (
              <label
                key={p.id}
                htmlFor={`product-${p.id}`}
                data-testid={`l1-order-product-${p.id}`}
                className={cn(
                  "flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors",
                  checked
                    ? "border-blue-600 bg-blue-50"
                    : "border-slate-200 bg-white hover:border-blue-300"
                )}
              >
                <RadioGroupItem
                  id={`product-${p.id}`}
                  value={p.id}
                  className="mt-1 border-slate-400 text-blue-600"
                />
                <div className="flex-1">
                  <div className="font-medium text-slate-900 text-base leading-snug">
                    {p.name}
                    {p.price && (
                      <span className="text-blue-700 font-semibold">
                        {" "}— {p.price}
                      </span>
                    )}
                  </div>
                  {p.description && (
                    <div className="text-sm text-slate-600 mt-1 leading-relaxed">
                      {p.description}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </RadioGroup>
      </div>

      <Button
        type="submit"
        data-testid="l1-order-submit"
        className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-md px-6 h-12 text-base"
      >
        <ExternalLink className="mr-2 h-4 w-4" />
        Fortsätt till säker betalning
        <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
      <p className="text-xs text-slate-500">
        Betalning sker via Stripe (säker checkout).
      </p>
    </form>
  );
}
